import { css } from '@compiled/react';

import CheckIcon from '@atlaskit/icon/core/check-mark';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ChromeIcon } from '../assets/ChromeIcon';
import { DesktopIcon } from '../assets/DesktopIcon';

import { LoomOnboardingButton } from './loom-onboarding-button';
import { LoomOnboardingHeading } from './loom-onboarding-heading';
import { LoomOnboardingText } from './loom-onboarding-text';

const ChromeText = {
	title: 'Chrome extension',
	subtitle: 'Installs fast for a quick start',
	valueProps: ['Free', 'Personalized recording canvas', 'Camera bubble backgrounds', 'Blur Tool'],
};

const DesktopText = {
	title: 'Desktop app',
	subtitle: 'Higher quality video and additional features ',
	valueProps: [
		'Free',
		'Personalized recording canvas',
		'Camera bubble backgrounds & frames',
		'Draw Tool',
		'Speaker notes',
		'Highlight mouse clicks',
		'Record in HD (up to 4k)',
	],
};

// Uses a raw 16px border-radius to preserve the original design. The previous
// `border.radius.400` token was deprecated by ADS with no direct token
// replacement (see tokens CHANGELOG migration guide), so we drop into a
// Compiled `css` block on a wrapper element because xcss `borderRadius` only
// accepts a fixed set of tokens.
const cardCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.300'),
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: '16px',
	padding: token('space.500'),
	width: '100%',
	maxWidth: '24rem',
	marginInline: 'auto',
});

const valuePropListStyles = xcss({
	alignSelf: 'stretch',
	padding: 'space.0',
	margin: 'space.0',
	textAlign: 'left',
});

const buttonWrapperStyles = xcss({
	marginBlockStart: 'auto',
	alignSelf: 'stretch',
	marginInline: 'auto',
	width: '100%',
});

const subtitleWrapperStyles = xcss({
	marginBlockStart: '0.5em',
});

export const RecorderCard = ({ recorder }: { recorder: 'chrome' | 'desktop' }) => {
	const copy = recorder === 'chrome' ? ChromeText : DesktopText;

	return (
		<div css={cardCss}>
			{recorder === 'chrome' ? (
				<ChromeIcon isLargeImage={false} />
			) : (
				<DesktopIcon isLargeImage={false} />
			)}
			<Box>
				<LoomOnboardingHeading level={3}>{copy.title}</LoomOnboardingHeading>
				<Box xcss={subtitleWrapperStyles}>
					<LoomOnboardingText align="center">{copy.subtitle}</LoomOnboardingText>
				</Box>
			</Box>
			<Stack space="space.100" as="ul" xcss={valuePropListStyles}>
				{copy.valueProps.map((item) => (
					<Inline key={item} space="space.100" alignBlock="start" as="li">
						<CheckIcon label="" color={token('color.icon.subtle')} />
						<LoomOnboardingText inline>{item}</LoomOnboardingText>
					</Inline>
				))}
			</Stack>
			<Box xcss={buttonWrapperStyles}>
				<LoomOnboardingButton appearance={recorder === 'chrome' ? 'primary' : 'default'} fullWidth>
					{recorder === 'chrome' ? 'Install' : 'Download'}
				</LoomOnboardingButton>
			</Box>
		</div>
	);
};
